// src/services/anprEvent.api.ts

import apiConfig from 'http/services/api.config'
import { ApiResponse } from 'http/types/api-response'
import { ANPREvent } from 'domain/anpr-event'
import apiService from 'http/services/api.service'
import { ANPREventQueryParams } from '../types/anpr-event-query-param'
import { ANPREventCountResponse } from '../types/anpr-event-count-response'
import { HourlyEventCount } from 'domain/hourly-event-count'
import { DirectionEventCount } from 'domain/direction-event-count'

class ANPREventApiService {
  private readonly baseUrl: string

  constructor() {
    this.baseUrl = apiConfig.endpoints.anprEvents
  }

  /**
   * Get all ANPR events with pagination and filtering
   */
  public async getANPREvents(
    params?: ANPREventQueryParams
  ): Promise<ApiResponse<ANPREvent[]>> {
    return apiService.post<ApiResponse<ANPREvent[]>>(
      this.baseUrl + `/${params?.clientId}`,
      params
    )
  }
  /**
   * Get all ANPR events with pagination and filtering
   */
  public async getANPREventsCountByDate(
    params?: ANPREventQueryParams
  ): Promise<ApiResponse<ANPREventCountResponse>> {
    return apiService.post<ApiResponse<ANPREventCountResponse>>(
      this.baseUrl + `/events-count/${params?.clientId}`,
      params
    )
  }

  /**
   * Get ANPR event counts grouped by hour of day (0-23)
   */
  public async getANPREventsHourlyCounts(
    params?: ANPREventQueryParams
  ): Promise<ApiResponse<HourlyEventCount[]>> {
    return apiService.post<ApiResponse<HourlyEventCount[]>>(
      this.baseUrl + `/hourly-counts/${params?.clientId}`,
      params
    )
  }

  /**
   * Get ANPR event counts grouped by raw Direction value
   */
  public async getANPREventsDirectionCounts(
    params?: ANPREventQueryParams
  ): Promise<ApiResponse<DirectionEventCount[]>> {
    return apiService.post<ApiResponse<DirectionEventCount[]>>(
      this.baseUrl + `/direction-counts/${params?.clientId}`,
      params
    )
  }

  /**
   * Get every ANPR event ever recorded for a license plate, most recent first.
   * No date range — it's the plate's full timeline for this client.
   */
  public async getPlateHistory(
    clientId: string,
    licensePlate: string
  ): Promise<ApiResponse<ANPREvent[]>> {
    return apiService.get<ApiResponse<ANPREvent[]>>(
      `${this.baseUrl}/plate-history/${clientId}/${encodeURIComponent(licensePlate)}`
    )
  }

  /**
   * Get ANPR event by ID
   */
  public async getANPREventById(id: string): Promise<ANPREvent> {
    return apiService.get<ANPREvent>(`${this.baseUrl}/${id}`)
  }

  /**
   * Delete an ANPR event
   */
  public async deleteANPREvent(id: string): Promise<void> {
    return apiService.delete<void>(`${this.baseUrl}/${id}`)
  }
}

export const anprEventApiService = new ANPREventApiService()
export default anprEventApiService
